import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPInput from '../../../../../components/IPInput'
import IPModal from '../../../../../components/IPModal'
import IPSelectBox, { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import Table from '../../../../../components/Table'
import { getBrandApi, getCompanyApi } from '../../../components/api'
import { IBrand, ICompany } from '../../../components/models/models'
import { IProductVisibilityProduct } from '../models/models'
import { ProductVisibilityListApi } from '../services/productVisibilityListApi'
import VisibilityRecordsTable from './VisibilityRecordsTable'
import '../../BestSellerList/components/BestSellerList.scss'

const statusOptions: ISelectOption[] = [
  { label: 'Aktif', value: 1 },
  { label: 'Pasif', value: 0 },
]

function ProductVisibility(props: BaseComponentWithContextProps) {
  const [companies, setCompanies] = useState<ISelectOption[]>([])
  const [brands, setBrands] = useState<ISelectOption[]>([])
  const [selectedCompany, setSelectedCompany] = useState<ISelectOption>()
  const [selectedBrand, setSelectedBrand] = useState<ISelectOption>()
  const [selectedStatus, setSelectedStatus] = useState<ISelectOption>()
  const [quotaNo, setQuotaNo] = useState('')
  const [productSearch, setProductSearch] = useState('')

  const [products, setProducts] = useState<IProductVisibilityProduct[]>([])
  const [searched, setSearched] = useState(false)

  const [recordModal, setRecordModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<IProductVisibilityProduct>()

  useEffect(() => {
    getCompanies()
    getBrands()
    getList()
  }, [])

  const getCompanies = async () => {
    try {
      const companyList: ICompany[] = await getCompanyApi()
      setCompanies(companyList.map(company => ({ label: company.Name, value: company.Id })))
    } catch (err) {
      console.log('Fetch Companies Error:', err)
    }
  }

  const getBrands = async () => {
    try {
      const brandList: IBrand[] = await getBrandApi()
      setBrands(brandList.map(brand => ({ label: brand.Name, value: brand.Id })))
    } catch (err) {
      console.log('Fetch Brands Error:', err)
    }
  }

  const getList = async () => {
    props.showLoading()
    try {
      const res = await new ProductVisibilityListApi({}).productVisibilityList({
        CompanyId: selectedCompany ? +selectedCompany.value : undefined,
        QuotaNo: quotaNo.trim().length > 0 ? quotaNo.trim() : undefined,
        BrandId: selectedBrand ? +selectedBrand.value : undefined,
        ProductSearch: productSearch.trim().length > 0 ? productSearch.trim() : undefined,
        Enabled: selectedStatus ? selectedStatus.value === 1 : undefined,
      })
      setProducts(res && res.Products ? res.Products : [])
      setSearched(true)
      props.hideLoading()
    } catch (err: any) {
      setProducts([])
      setSearched(true)
      props.hideLoading()
      Swal.fire('Hata', err.description, 'error')
    }
  }

  const showRecords = (product: IProductVisibilityProduct) => {
    setSelectedProduct(product)
    setRecordModal(true)
  }

  const columns = useMemo(
    () => [
      { Header: 'Ürün Kodu', accessor: 'productCode' },
      { Header: 'Ürün Adı', accessor: 'productName' },
      { Header: 'Şirket', accessor: 'company' },
      { Header: 'Marka', accessor: 'brand' },
      { Header: 'Kota', accessor: 'quotaNo' },
      { Header: 'Malzeme Numarası', accessor: 'matNumber' },
      { Header: 'Gizlilik Durumu', accessor: 'status' },
      { Header: 'İstisna Bayi', accessor: 'exceptionDealerCount' },
      { Header: 'Ürün Durumu', accessor: 'productStatus' },
      { Header: 'Kayıtlar', accessor: 'records' },
    ],
    []
  )

  // Herkese kapalı kaydın yanında bayi istisnası varsa ürün gerçekte "herkesten gizli" değildir;
  // yalnızca istisna kaydı olan bayiler görebilir.
  const renderStatus = (product: IProductVisibilityProduct) => {
    if (product.HiddenForAll && product.ExceptionDealerCount > 0)
      return <b style={{ color: 'orange' }}>Bazı bayilere görünür</b>

    if (product.HiddenForAll) return <b style={{ color: 'red' }}>Herkesten gizli</b>

    return <b style={{ color: 'green' }}>Gizleme yok (yalnız istisna kaydı)</b>
  }

  const tableData = useMemo(
    () =>
      products.map(product => ({
        productCode: (
          <Link target='_blank' to={`/product/detail/${product.ProductId}?from=productList`}>
            {product.ProductCode}
          </Link>
        ),
        productName: product.ProductName,
        company: product.Company,
        brand: product.Brand,
        quotaNo: product.QuotaNo,
        matNumber: product.MatNumber,
        status: renderStatus(product),
        exceptionDealerCount: product.ExceptionDealerCount,
        productStatus: product.Deleted ? (
          <b style={{ color: 'red' }}>Silinmiş</b>
        ) : product.Enabled ? (
          <b style={{ color: 'green' }}>Aktif</b>
        ) : (
          <b style={{ color: 'orange' }}>Pasif</b>
        ),
        records: (
          <b
            style={{ color: '#0055a5', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => showRecords(product)}
          >
            {`Kayıtlar (${product.Records ? product.Records.length : 0})`}
          </b>
        ),
      })),
    [products]
  )

  return (
    <div className='container' style={{ maxWidth: '100%' }}>
      <div className='card-header'>
        <span className='h4'>Ürün Gizlilik Kayıtları</span>
      </div>
      <div className='card-body'>
        <div className='row'>
          <div className='col-md-3'>
            <label className='form-label'>Şirket</label>
            <IPSelectBox
              name='company'
              options={companies}
              value={selectedCompany}
              placeholder='Şirket Seçiniz...'
              onChangeSingle={option => setSelectedCompany(option)}
              isClearable
            />
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Marka</label>
            <IPSelectBox
              name='brand'
              options={brands}
              value={selectedBrand}
              placeholder='Marka Seçiniz...'
              onChangeSingle={option => setSelectedBrand(option)}
              isClearable
            />
          </div>
          <div className='col-md-2'>
            <label className='form-label'>Kota</label>
            <IPInput
              type='text'
              className='form-control'
              name='quotaNo'
              value={quotaNo}
              onChange={e => setQuotaNo(e.target.value)}
            />
          </div>
          <div className='col-md-2'>
            <label className='form-label'>Ürün Durumu</label>
            <IPSelectBox
              name='status'
              options={statusOptions}
              value={selectedStatus}
              placeholder='Tümü'
              onChangeSingle={option => setSelectedStatus(option)}
              isClearable
            />
          </div>
          <div className='col-md-2'>
            <label className='form-label'>Ürün</label>
            <IPInput
              type='text'
              className='form-control'
              name='productSearch'
              placeholder='Ürün Kodu / Malzeme No / Ürün Adı'
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
            />
          </div>
        </div>
        <br />
        <div className='row d-flex justify-content-end'>
          <div className='col-md-2'>
            <IPButton type='button' className='btn btn-success' text='Sorgula' onClick={getList} />
          </div>
        </div>
        <br />
        <div className='row'>
          <div className='col-12'>
            <small>
              Listede yalnızca SAP tarafında <b>tarihi geçerli</b> gizlilik kaydı bulunan ürünler yer alır. Herkese
              kapalı kaydın yanında bir bayiye ait FLAGS = 1 istisna kaydı varsa ürün o bayiler için görünür kalır.
              {searched && <b>{` Toplam ${products.length} ürün.`}</b>}
            </small>
          </div>
        </div>
      </div>
      <div className='col-lg-12'>
        {searched && products.length === 0 ? (
          <div className='alert alert-warning'>Filtreye uyan, tarihi geçerli gizlilik kaydı olan ürün bulunamadı.</div>
        ) : (
          <Table columns={columns} data={tableData} currentPage={0} />
        )}
      </div>
      <IPModal
        show={recordModal}
        onClose={() => {
          setRecordModal(false)
          setSelectedProduct(undefined)
        }}
        hideConfirm={true}
        width={1200}
        title={`Gizlilik Kayıtları - ${selectedProduct ? selectedProduct.ProductCode : ''} (${
          selectedProduct ? selectedProduct.MatNumber : ''
        })`}
      >
        <VisibilityRecordsTable records={selectedProduct && selectedProduct.Records ? selectedProduct.Records : []} />
      </IPModal>
    </div>
  )
}

export default componentWithContext(ProductVisibility)
